import React from 'react';
import '../styles/allmetal.css';
import '../styles/services.css';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  FaIndustry, FaCheckCircle, FaArrowRight, FaChevronRight,
  FaCog, FaWarehouse, FaRecycle, FaShieldAlt, FaClock,
  FaLeaf, FaTruck, FaFileAlt
} from 'react-icons/fa';

const fadeUp    = { hidden:{opacity:0,y:50},  show:{opacity:1,y:0,  transition:{duration:0.6,ease:[0.22,1,0.36,1]}} };
const fadeLeft  = { hidden:{opacity:0,x:-55}, show:{opacity:1,x:0,  transition:{duration:0.6,ease:[0.22,1,0.36,1]}} };
const fadeRight = { hidden:{opacity:0,x:55},  show:{opacity:1,x:0,  transition:{duration:0.6,ease:[0.22,1,0.36,1]}} };
const stagger   = { hidden:{}, show:{ transition:{ staggerChildren:0.1, delayChildren:0.05 } } };

function Section({ children, className='' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once:true, margin:'-80px' });
  return (
    <motion.section ref={ref} className={className}
      variants={stagger} initial="hidden" animate={inView?'show':'hidden'}>
      {children}
    </motion.section>
  );
}

const metals = [
  { icon:<FaCog />,       title:'Ferrous Metals',     text:'Steel, iron & alloys — collected, sorted and recycled at scale with full documentation.' },
  { icon:<FaWarehouse />, title:'Non-Ferrous Metals',  text:'Aluminium, copper, brass & more — higher value recovery with responsible processing.' },
  { icon:<FaRecycle />,   title:'Mixed Scrap',         text:'Cable, e-waste & industrial scrap handled responsibly — nothing goes to landfill.' },
];

const checkPoints = [
  { icon:<FaCheckCircle />, text:'Ferrous & Non-Ferrous Metals — steel, aluminium, copper & more' },
  { icon:<FaCheckCircle />, text:'Scrap Metal Collection — convenient on-site pickup & transport' },
  { icon:<FaCheckCircle />, text:'Sustainable Practices — responsible methods, minimal landfill' },
  { icon:<FaCheckCircle />, text:'Bulk & Commercial Pickup available' },
  { icon:<FaCheckCircle />, text:'Eco-Certified Processing with full documentation' },
];

const Allmetal = () => {
  const navigate = useNavigate();

  return (
    <div className="services-page">

      {/* ── Hero ── */}
      <section className="page-hero" style={{ backgroundImage:"url('/Assets/kmkimg2.jpg')" }}>
        <div className="ph-overlay" />
        <div className="ph-particles" aria-hidden="true">
          {[...Array(5)].map((_,i)=><span key={i} className={`ph-part ph-part--${i+1}`}/>)}
        </div>
        <span className="ph-glow-a" /><span className="ph-glow-b" />
        <div className="container ph-inner">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.nav className="ph-breadcrumb" variants={fadeUp}>
              <Link to="/">Home</Link>
              <FaChevronRight className="ph-crumb-sep" />
              <span>All Metal</span>
            </motion.nav>
            <motion.p className="ph-eyebrow" variants={fadeUp}>Recycling Services</motion.p>
            <motion.h1 className="ph-title" variants={fadeUp}>
              All Metal <span className="ph-title-accent">Disposition</span>
            </motion.h1>
            <motion.p className="ph-sub" variants={fadeUp}>
              Ferrous and non-ferrous metals, recycled and repurposed with eco-certified practices —
              tailored for businesses of every scale.
            </motion.p>
            <motion.div className="ph-actions" variants={fadeUp}>
              <button className="ph-btn-primary" onClick={()=>navigate('/contact')}>
                Request Pickup <FaArrowRight />
              </button>
              <button className="ph-btn-outline" onClick={()=>navigate('/services')}>
                All Services
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Content ── */}
      <Section className="services-body">
        <div className="container">
          <motion.div className="services-intro" variants={fadeUp}>
            <p className="eyebrow">Trusted Metal Recyclers</p>
            <h2>Your Partner for All <span className="text-green">Metal Recycling</span></h2>
            <p className="sub">
              From steel and aluminium to copper and beyond — every metal is processed in the most
              eco-friendly way possible, with full documentation.
            </p>
          </motion.div>

          {/* Split layout */}
          <div className="am-split">
            <motion.div className="am-visual" variants={fadeLeft}>
              <div className="am-img-wrap">
                <img src="/Assets/imgkmk13.jpg" alt="All Metal Disposition" loading="lazy" />
                <span className="am-tag"><FaIndustry /> All Metals</span>
                <div className="am-glow" />
              </div>
            </motion.div>

            <motion.div className="am-content" variants={fadeRight}>
              <h3>What We Handle</h3>
              <p>
                We are your trusted partner for all metal disposition needs. Every consignment is
                weighed, graded and routed to the right recovery stream — maximising value while
                keeping waste out of landfill.
              </p>
              <ul className="svc-page-list">
                {checkPoints.map((pt, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity:0, x:-20 }}
                    whileInView={{ opacity:1, x:0 }}
                    viewport={{ once:true }}
                    transition={{ delay: i*0.08, duration:0.4 }}
                  >
                    <FaCheckCircle /> {pt.text}
                  </motion.li>
                ))}
              </ul>
              <a href="/contact" className="svc-page-btn">
                Request a Pickup <FaArrowRight />
              </a>
            </motion.div>
          </div>

          {/* Metal-type cards */}
          <div className="am-cards">
            {metals.map((m, i) => (
              <motion.div
                className="am-card"
                key={i}
                variants={fadeUp}
                custom={i}
                whileHover={{ y:-8, transition:{duration:0.25} }}
              >
                <span className="am-card-icon">{m.icon}</span>
                <h4>{m.title}</h4>
                <p>{m.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── CTA ── */}
      <section className="page-cta">
        <div className="container cta-inner">
          <motion.div
            className="cta-text"
            initial={{ opacity:0, y:40 }}
            whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }}
            transition={{ duration:0.65 }}
          >
            <motion.span
              className="cta-icon"
              animate={{ rotate:[0,10,-10,8,-8,0] }}
              transition={{ duration:2, repeat:Infinity, repeatDelay:3 }}
            >
              <FaIndustry />
            </motion.span>
            <h2>Have Scrap to <span className="text-green">Recycle?</span></h2>
            <p>Get a fair, transparent valuation and fast on-site pickup for your metal scrap.</p>
            <button className="ph-btn-primary" onClick={()=>navigate('/contact')}>
              Get Free Quote <FaArrowRight />
            </button>
            <div className="cta-trust">
              <span><FaCheckCircle /> No obligation</span>
              <span><FaShieldAlt /> 100% secure</span>
              <span><FaClock /> Quick response</span>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Allmetal;
